import { FTMigration, useIntl, useSettings } from '@capsicum/common';
import { BoldText, Image, Text } from '@capsicum/common-web';
import { AccessIcon, ExternalLinkIcon, FtoIcon } from '@capsicum/common-web/icons';
import { type V2FTMigrationModalContentProps } from '@capsicum/features/nc-wallet/shared';
import { useId, useMemo } from 'react';
import styled from 'styled-components';

import { V2MigrationFanTokenLogo } from './V2MigrationFanTokenLogo';

export const V2FTMigrationModalContent = ({ tokens: tokensProp }: V2FTMigrationModalContentProps) => {
	const { $t } = useIntl();
	const tokensSectionID = useId();

	const {
		settings: { v2FFTMigrationFAQExternalLink }
	} = useSettings();

	const tokens = useMemo(
		() =>
			tokensProp.map(token => {
				return (
					<S.Token key={token.shortCode}>
						<V2MigrationFanTokenLogo
							logo={token.logo}
							name={token.name}
							shortCode={token.shortCode}
							size={16}
						/>
						<Text variant="text3">{token.name}</Text>
					</S.Token>
				);
			}),
		[tokensProp]
	);

	return (
		<S.Wrapper>
			<Image alt="v2Fan Token Migration" src={FTMigration} width={172} />

			<S.Content>
				<S.Header>
					<S.TextCenterAlign variant="title6">{$t('v2FTMigrationInitFlow.modal.title')}</S.TextCenterAlign>

					<S.TextCenterAlign variant="text3">
						{$t<JSX.Element>('v2FTMigrationInitFlow.modal.description', {
							bold: chunks => <BoldText>{chunks}</BoldText>
						})}
					</S.TextCenterAlign>
				</S.Header>

				<S.Body>
					<S.Section>
						<AccessIcon />

						<S.SectionContent>
							<Text variant="text1">{$t('v2FTMigrationInitFlow.modal.sections.benefits.title')}</Text>

							<Text variant="text3">
								{$t<JSX.Element>('v2FTMigrationInitFlow.modal.sections.benefits.description', {
									link: chunks => (
										<S.LinkText
											data-testid="v2FTMigrationModalContent-faq-link"
											href={v2FFTMigrationFAQExternalLink}
											target="_blank"
										>
											{chunks}

											<S.StyledExternalLinkIcon width={20} />
										</S.LinkText>
									)
								})}
							</Text>
						</S.SectionContent>
					</S.Section>

					<S.Section>
						<FtoIcon />

						<S.SectionContent aria-labelledby={tokensSectionID}>
							<Text id={tokensSectionID} variant="text1">
								{$t('v2FTMigrationInitFlow.modal.sections.tokens.title')}
							</Text>

							<S.SectionTokens>{tokens}</S.SectionTokens>
						</S.SectionContent>
					</S.Section>
				</S.Body>
			</S.Content>
		</S.Wrapper>
	);
};

namespace S {
	export const Wrapper = styled.div`
		display: flex;
		align-items: center;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.large};
	`;

	export const Content = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.xlarge};
	`;

	export const Header = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const TextCenterAlign = styled(Text)`
		text-align: center;
	`;

	export const Body = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.small};
	`;

	export const Section = styled.div`
		width: 100%;
		display: flex;
		gap: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const LinkText = styled.a`
		display: inline;
		color: ${({ theme }) => theme.colors.text.brand.enabled};
	`;

	export const StyledExternalLinkIcon = styled(ExternalLinkIcon)`
		display: inline;
		transform: translateY(7px);
	`;

	export const SectionContent = styled.div`
		flex: 1;
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const SectionTokens = styled.div`
		display: flex;
		flex-wrap: wrap;
		gap: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const Token = styled.div`
		display: flex;
		align-items: center;
		gap: ${({ theme }) => theme.spacings.xxxsmall};
		flex: ${({ theme }) => `calc(50% - ${theme.spacings.xxxsmall})`};
	`;
}
